import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { BRAND } from '@shop/shared'

type BuildInfo = { version: string; shortCommit: string }

/**
 * ختمُ البناء — يُسأل دوريًّا، فتغيّرُه يعني أنّ نشرةً وصلت.
 *
 * ولا يُخفى عند الخطأ: خادمٌ لا يردّ على هذه النقطة البسيطة خبرٌ يحتاجه
 * الموظّفُ قبل أن يُملي عشرةَ أصناف ويكتشف أنّ شيئًا لم يُحفظ.
 */
function BuildBadge() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['build'],
    queryFn: () => api<BuildInfo>('/api/v1/build', { anonymous: true }),
    refetchInterval: 60_000,
  })

  return (
    <span className={`tabular text-xs ${isError ? 'text-danger' : 'text-ink-soft'}`} dir="ltr">
      {isPending ? '…' : isError ? 'الخادم لا يردّ' : `${data!.version} · ${data!.shortCommit}`}
    </span>
  )
}

type NavItem = { to: string; label: string; end: boolean; needs: string | null }

/**
 * التنقّلُ في مجموعات — <b>خمسٌ في الواجهة، والباقي في «الإعدادات»</b>.
 *
 * <p>
 * ⚠ <b>قرارُ المالك ٢٠٢٦-٠٩-٢٨: «بدي اتسهل البرنامج، ما بدي كتير تفاصيل».</b>
 * كان هنا ثلاثون رابطًا في شريطٍ واحدٍ يتمرّر أفقيًّا، فلا يُرى نصفُها ولا
 * يُعرف أين الشاشةُ المطلوبة. فصارت مجموعاتٍ على صورة عمل المحلّ نفسِه:
 * بضاعةٌ تدخل، ومشترياتٌ تُفوتَر، وصندوقٌ يُصرف منه، وتقاريرُ تُقرأ.
 * </p>
 *
 * <p>
 * ⚠ <b>ولا يُحذف مسارٌ واحد</b> — تُنقل الشاشاتُ النادرةُ إلى «الإعدادات»
 * وتبقى تعمل برابطها. فالحذفُ يهدم تقاريرَ تعتمد عليها، والإخفاءُ يُرجَع عنه
 * بسطر. <b>والتبسيطُ في الواجهة لا في النظام.</b>
 * </p>
 *
 * <p>
 * والتنقّلُ يُخفي ما لا يحقّ للموظّف — <b>تحسينًا للشاشة لا حراسةً</b>. فالحراسةُ
 * على الخادم في كلّ نقطة، وهذا يمنع أن يبذل الموظّفُ عملَه ثمّ يُصدَم بـ403.
 * </p>
 */
const groups: { id: string; label: string; items: NavItem[] }[] = [
  {
    id: 'home',
    label: 'اللوحة',
    items: [
      { to: '/', label: 'اللوحة', end: true, needs: null },
      // ⚠ **ولكلّ من يدخل** — الكاشيرُ يثبّته على جهازه كما المالك.
      { to: '/windows-app', label: 'برنامج ويندوز', end: false, needs: null },
    ],
  },
  {
    // ⚠ مجموعةٌ في الواجهة لا تحت «الإعدادات»: طلبٌ ينتظر في قائمةٍ لا تُفتح
    //   زبونٌ ضاع. والشارةُ على اسمها تقول كم ينتظر.
    id: 'orders',
    label: 'طلباتُ الموقع',
    items: [
      { to: '/web-orders', label: 'طلباتُ الموقع', end: false, needs: Perm.webOrderView },
      { to: '/social/new', label: 'منشورٌ جديد', end: false, needs: Perm.socialPublish },
      { to: '/social', label: 'حساباتُ التواصل', end: true, needs: Perm.socialPublish },
    ],
  },
  {
    id: 'stock',
    label: 'البضاعة',
    items: [
      { to: '/stocktake', label: 'استلامٌ وجرد', end: false, needs: Perm.draftConfirm },
      { to: '/products', label: 'الأصناف', end: false, needs: Perm.productView },
      { to: '/products/new', label: 'صنفٌ جديد', end: false, needs: Perm.productCreate },
      { to: '/stock', label: 'المخزون', end: false, needs: Perm.movementView },
      { to: '/item-movement', label: 'حركةُ المادّة', end: false, needs: Perm.reportViewMine },
      { to: '/categories', label: 'الأقسام', end: false, needs: Perm.productView },
      { to: '/publishing', label: 'النشرُ على الموقع', end: false, needs: Perm.productPublish },
      { to: '/offers', label: 'العروض', end: false, needs: Perm.productView },
    ],
  },
  {
    id: 'purchase',
    label: 'المشتريات',
    items: [
      { to: '/purchase-bills', label: 'فواتيرُ المشتريات', end: false, needs: Perm.receiptView },
      { to: '/purchase-invoice', label: 'تسعيرُ ما استُلم بلا كلفة', end: false, needs: Perm.receiptPost },
      { to: '/uncosted', label: 'أصنافٌ بلا كلفة', end: false, needs: Perm.costView },
      { to: '/suppliers', label: 'المورّدون', end: false, needs: Perm.supplierView },
    ],
  },
  {
    id: 'cash',
    label: 'الصندوق',
    items: [
      { to: '/cashbook', label: 'مصاريفُ ودفعات', end: false, needs: Perm.journalPost },
      { to: '/balances', label: 'الأرصدة — مورّدون وموظّفون', end: false, needs: Perm.ledgerView },
      { to: '/statement', label: 'كشفُ حساب', end: false, needs: Perm.ledgerView },
      { to: '/attendance', label: 'الدوام', end: false, needs: Perm.attendancePunch },
      { to: '/offline-ops', label: 'عمليّاتٌ دون اتّصال', end: false, needs: Perm.cashSessionView },
      { to: '/receivables', label: 'ذممُ الزبائن', end: false, needs: Perm.customerView },
    ],
  },
  {
    id: 'reports',
    label: 'التقارير',
    items: [
      { to: '/reports', label: 'التقارير', end: false, needs: Perm.reportViewMine },
      { to: '/cashier-sales', label: 'مبيعاتُ الموظّفين', end: false, needs: Perm.reportViewMine },
      { to: '/payroll', label: 'رواتبُ الموظّفين', end: false, needs: Perm.commissionView },
    ],
  },
  {
    // ⚠ **وما هنا يعمل كما كان** — لم يُحذف منه شيء. نُقل من الواجهة فقط.
    id: 'settings',
    label: 'الإعدادات',
    items: [
      { to: '/staff', label: 'الموظّفون', end: false, needs: Perm.userView },
      { to: '/roles', label: 'الصلاحيّات', end: false, needs: Perm.roleView },
      { to: '/sales-settings', label: 'إعداداتُ البيع', end: false, needs: Perm.saleView },
      { to: '/warehouses', label: 'المستودعات', end: false, needs: Perm.warehouseView },
      { to: '/opening', label: 'فترةُ الافتتاح', end: false, needs: Perm.movementView },
      { to: '/adjustments', label: 'التسويات', end: false, needs: Perm.adjustmentView },
      { to: '/accounting', label: 'الدفاتر', end: false, needs: Perm.ledgerView },
      { to: '/purchases', label: 'أوامرُ الشراء', end: false, needs: Perm.purchaseView },
      { to: '/receipts', label: 'إيصالاتُ الاستلام', end: false, needs: Perm.receiptView },
      { to: '/supplier-returns', label: 'مرتجعاتُ المورّدين', end: false, needs: Perm.returnView },
      { to: '/transfers', label: 'التحويلات', end: false, needs: Perm.transferView },
      { to: '/board', label: 'لوحةُ المخزون', end: false, needs: Perm.movementView },
      { to: '/commissions', label: 'العمولات', end: false, needs: Perm.commissionView },
      { to: '/intake', label: 'إدخالٌ بالمسوّدة', end: false, needs: Perm.draftCreate },
      { to: '/drafts', label: 'مسوّداتي', end: false, needs: Perm.draftView },
    ],
  },
]

/**
 * المجموعةُ التي تحوي الصفحةَ الحاليّة — <b>بأطولِ بادئةٍ تطابق</b>.
 *
 * ⚠ و«الأطول» لا «الأولى»: `/products/new` تطابق `/products` أيضًا، ولو أُخذت
 *   الأولى لَظُلّل رابطٌ غيرُ المفتوح.
 */
function activeGroup(path: string): string {
  let best = { id: 'home', length: -1 }

  for (const group of groups) {
    for (const item of group.items) {
      const hit = item.end ? path === item.to : path === item.to || path.startsWith(`${item.to}/`)

      if (hit && item.to.length > best.length) {
        best = { id: group.id, length: item.to.length }
      }
    }
  }

  return best.id
}

export default function Shell() {
  const { session, logout, can } = useAuth()
  const { pathname } = useLocation()

  // ⚠ **ومجموعةٌ لا رابطَ فيها يحقّ للموظّف لا تُعرض أصلًا** — فالكاشيرُ لا
  //   يرى «المشتريات» فارغةً ثمّ يتساءل لماذا.
  const visible = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.needs === null || can(item.needs)),
    }))
    .filter((group) => group.items.length > 0)

  const current = activeGroup(pathname)

  // الطلباتُ الجديدةُ من الموقع — رقمٌ على اسم المجموعة، يُسأل كلَّ دقيقة.
  const waiting = useQuery({
    queryKey: ['web-orders', 'new-count'],
    queryFn: () => api<{ count: number }>('/api/v1/web-orders/new-count'),
    enabled: can(Perm.webOrderView),
    refetchInterval: 60_000,
  })
  const newOrders = waiting.data?.count ?? 0
  const open = visible.find((group) => group.id === current)

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-line bg-surface-raised">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-semibold">{BRAND.nameAr}</span>
            <BuildBadge />
          </div>

          <div className="flex items-center gap-3 text-xs text-ink-soft">
            <span dir="ltr">{session?.me.username}</span>
            <button className="underline underline-offset-4 hover:text-ink" onClick={() => void logout()}>
              خروج
            </button>
          </div>
        </div>

        {/* ── المجموعات ─────────────────────────────────────────────── */}
        <nav className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 text-sm">
          {visible.map((group) => (
            <NavLink
              key={group.id}
              to={group.items[0].to}
              end={group.items[0].end}
              className={
                `whitespace-nowrap rounded-t-md px-4 py-2 font-semibold ${
                  group.id === current
                    ? 'bg-surface text-ink'
                    : 'text-ink-soft hover:text-ink'
                }`
              }
            >
              {group.label}
              {group.id === 'orders' && newOrders > 0 && (
                <span className="tabular mr-1.5 rounded-full bg-danger px-1.5 text-xs text-white">{newOrders}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* ── ما في المجموعة المفتوحة ──────────────────────────────────── */}
        {open && open.items.length > 1 && (
          <nav className="bg-surface">
            <div className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 py-2 text-sm">
              {open.items.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `whitespace-nowrap rounded-md px-3 py-1.5 ${
                      isActive ? 'bg-brand text-white' : 'text-ink-soft hover:bg-surface-raised'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 space-y-4 px-4 py-6">
        <Outlet />
      </main>

      <footer className="border-t border-line px-4 py-3 text-center text-xs text-ink-soft">
        <span>{BRAND.nameAr}</span>
        <span className="mx-2 opacity-50">·</span>
        <span dir="ltr">{BRAND.nameEn}</span>
      </footer>
    </div>
  )
}
